import {
  CreateCustomChartRequest,
  CustomChartResponse,
  CustomChartType,
  TaskListMode,
} from 'compliance-api';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import { useIncidentsTypesListQuery } from '@/modules/common/queries/incidents.ts';
import { useTasksListQuery } from '@/modules/common/queries/tasks.ts';
import {
  useCreateCustomChartMutation,
  useFormFieldsQuery,
  useUpdateCustomChartMutation,
} from '@/modules/admin-dashboard/queries/custom-charts.ts';
import {
  BaseModalProps,
  Button,
  ModalContentWrapper,
  Select,
  SelectOption,
  SelectPaginate,
  SelectPaginateOption,
  TextInputField,
  TooltipNew,
} from 'ui';

enum DataSourceType {
  Task = 'task',
  Event = 'event',
}

const dataSourceOptions: SelectOption<DataSourceType>[] = [
  { label: 'Task', value: DataSourceType.Task },
  { label: 'Event', value: DataSourceType.Event },
];

// const chartTypeOptions: SelectOption<CustomChartType>[] = [
//   { label: 'Pie Chart', value: CustomChartType.Pie },
// ];

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Chart name is required').max(70),
  group_by: Yup.string().required('Group by is required'),
  metric: Yup.string().required('Metric is required'),
  chart_type: Yup.string().required('Show results as is required'),
  task_id: Yup.number().when('dataSource', {
    is: DataSourceType.Task,
    then: schema => schema.required('Task is required').min(1),
    otherwise: schema => schema.nullable(),
  }),
  incident_type_id: Yup.number().when('dataSource', {
    is: DataSourceType.Event,
    then: schema => schema.required('Event is required').min(1),
    otherwise: schema => schema.nullable(),
  }),
});

interface CreateCustomChartModalProps extends BaseModalProps {
  chart?: CustomChartResponse;
}

type CustomChartFormValues = {
  name: string;
  group_by: string;
  metric: string;
  chart_type: CustomChartType;
  dataSource: DataSourceType;
  task_id: number | null;
  incident_type_id: number | null;
};

export const CreateCustomChartModal = ({ open, onClose, chart }: CreateCustomChartModalProps) => {
  const [selectedTask, setSelectedTask] = useState<SelectPaginateOption<number> | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<SelectPaginateOption<number> | null>(null);

  const formik = useFormik<CustomChartFormValues>({
    initialValues: {
      name: '',
      group_by: '',
      metric: '',
      chart_type: CustomChartType.Pie,
      dataSource: DataSourceType.Task,
      task_id: null,
      incident_type_id: null,
    },
    validationSchema,
    validateOnChange: false,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async values => {
      const isTask = values.dataSource === DataSourceType.Task;
      const payload: CreateCustomChartRequest = {
        name: values.name,
        group_by: values.group_by,
        metric: values.metric,
        chart_type: values.chart_type,
        task_id: isTask ? values.task_id || undefined : undefined,
        event_type_id: !isTask ? values.incident_type_id || undefined : undefined,
      };

      if (chart?.id) {
        await updateCustomChart({
          id: chart.id,
          ...payload,
        });
      } else {
        await createCustomChart(payload);
      }
      handleCloseModal();
    },
  });

  const { fetchNextPage: fetchNextTasksPage } = useTasksListQuery({
    page: 1,
    page_size: 10000,
    mode: TaskListMode.AllTasks,
  });

  const { fetchNextPage: fetchNextEventsPage } = useIncidentsTypesListQuery({
    page: 1,
    page_size: 10000,
  });

  const { data: formFieldsData, isFetching: isFetchingFormFields } = useFormFieldsQuery(
    {
      task_id: formik.values.task_id || undefined,
      incident_type_id: formik.values.incident_type_id || undefined,
    },
    {
      enabled: !!formik.values.task_id || !!formik.values.incident_type_id,
    },
  );

  const { mutateAsync: createCustomChart, isLoading: isCreating } = useCreateCustomChartMutation(
    formik.setErrors,
  );
  const { mutateAsync: updateCustomChart, isLoading: isUpdating } = useUpdateCustomChartMutation(
    formik.setErrors,
  );

  const isLoading = isCreating || isUpdating;

  const handleFetchTasks = async (page: number) => {
    const res = await fetchNextTasksPage({ pageParam: page });
    const lastPage = res.data?.pages.at(-1);
    return lastPage?.data.map(({ id, name }) => ({ label: name, value: id })) || [];
  };

  const handleFetchEvents = async (page: number, search?: string) => {
    const res = await fetchNextEventsPage({ pageParam: { page, search } });
    const lastPage = res.data?.pages.at(-1);
    return lastPage?.data.map(({ id, name }) => ({ label: name, value: id })) || [];
  };

  const groupByOptions: SelectOption<string>[] = useMemo(
    () => [
      { label: 'Creator', value: 'task_or_event_submitter' },
      ...(formFieldsData?.fields.map(field => ({
        label: field.label,
        value: field.label,
      })) || []),
    ],
    [formFieldsData],
  );

  const metricOptions = useMemo(
    () =>
      formFieldsData?.fields.map(field => {
        const isDisabled = !field.is_number;
        return {
          label: (
            <TooltipNew
              text="Only numeric fields can be selected as a metric."
              disabled={!isDisabled}
            >
              <div className={isDisabled ? 'text-gray-400 cursor-not-allowed' : ''}>
                {field.label}
              </div>
            </TooltipNew>
          ),
          value: field.label,
          isDisabled,
        };
      }) || [],
    [formFieldsData],
  );

  const handleCloseModal = () => {
    formik.resetForm();
    setSelectedTask(null);
    setSelectedEvent(null);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setSelectedTask(null);
      setSelectedEvent(null);
      return;
    }

    if (chart) {
      const isTask = !!chart.task_id;
      formik.setValues({
        name: chart.name,
        group_by: chart.group_by,
        metric: chart.metric,
        chart_type: chart.chart_type as CustomChartType,
        dataSource: isTask ? DataSourceType.Task : DataSourceType.Event,
        task_id: chart.task_id ?? null,
        incident_type_id: chart.event_type_id ?? null,
      });

      if (isTask && chart.task_id) {
        (async () => {
          const res = await fetchNextTasksPage({ pageParam: 1 });
          const allTasks = res.data?.pages.flatMap(p => p.data);
          const task = allTasks?.find(t => t.id === chart.task_id);
          if (task) {
            setSelectedTask({ label: task.name, value: task.id });
          }
        })();
      } else if (!isTask && chart.event_type_id) {
        (async () => {
          const res = await fetchNextEventsPage({ pageParam: { page: 1 } });
          const allEvents = res.data?.pages.flatMap(p => p.data);
          const event = allEvents?.find(e => e.id === chart.event_type_id);
          if (event) {
            setSelectedEvent({ label: event.name, value: event.id });
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart, open]);

  return (
    <ModalContentWrapper
      title={chart ? 'Update Custom Chart' : 'Create Custom Chart'}
      open={open}
      onClose={handleCloseModal}
    >
      <FormikProvider value={formik}>
        <form className="w-full flex flex-col space-y-10" onSubmit={e => e.preventDefault()}>
          <div className="flex flex-col gap-4">
            <TextInputField name="name" label="Chart Name" disabled={isLoading} />
            <Select
              label="Data Source"
              options={dataSourceOptions}
              value={formik.values.dataSource}
              onChange={value => {
                formik.setFieldValue('dataSource', value);
                formik.setFieldValue('task_id', null);
                formik.setFieldValue('incident_type_id', null);
                formik.setFieldValue('metric', '');
                formik.setFieldValue('group_by', '');
                setSelectedTask(null);
                setSelectedEvent(null);
              }}
              disabled={isLoading || !!chart}
            />

            {formik.values.dataSource === DataSourceType.Task && (
              <SelectPaginate
                label="Task"
                onAsync={handleFetchTasks}
                value={selectedTask}
                onChange={option => {
                  setSelectedTask(option);
                  formik.setFieldValue('task_id', option?.value || null);
                  formik.setFieldValue('metric', '');
                  formik.setFieldValue('group_by', '');
                }}
                error={formik.touched.task_id ? (formik.errors.task_id as string) : undefined}
                isClearable
                disabled={isLoading || !!chart}
              />
            )}

            {formik.values.dataSource === DataSourceType.Event && (
              <SelectPaginate
                label="Event"
                onAsync={handleFetchEvents}
                value={selectedEvent}
                onChange={option => {
                  setSelectedEvent(option);
                  formik.setFieldValue('incident_type_id', option?.value || null);
                  formik.setFieldValue('metric', '');
                  formik.setFieldValue('group_by', '');
                }}
                error={
                  formik.touched.incident_type_id
                    ? (formik.errors.incident_type_id as string)
                    : undefined
                }
                isClearable
                disabled={isLoading || !!chart}
              />
            )}

            <Select
              label="Group By"
              options={groupByOptions}
              value={formik.values.group_by}
              onChange={value => formik.setFieldValue('group_by', value)}
              error={formik.touched.group_by ? (formik.errors.group_by as string) : undefined}
              disabled={isLoading || isFetchingFormFields || !groupByOptions.length}
              placeholder={isFetchingFormFields ? 'Loading...' : 'Select a field'}
            />

            <Select
              label="Metric"
              options={metricOptions}
              value={formik.values.metric}
              onChange={value => {
                const selectedOption = metricOptions.find(o => o.value === value);
                if (selectedOption?.isDisabled) return;
                formik.setFieldValue('metric', value);
              }}
              error={formik.touched.metric ? (formik.errors.metric as string) : undefined}
              disabled={isLoading || isFetchingFormFields || !metricOptions.length}
              placeholder={isFetchingFormFields ? 'Loading...' : 'Select a metric'}
            />

            {/*<Select
              label="Show Results As"
              options={chartTypeOptions}
              value={formik.values.chart_type}
              onChange={value => formik.setFieldValue('chart_type', value)}
              disabled={isLoading}
            />*/}
          </div>
          <div className="flex justify-end gap-4 mt-8">
            <Button variant="secondary" onClick={handleCloseModal} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={formik.submitForm} disabled={isLoading} loading={isLoading}>
              Save
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};
